/**
 * PushAlertsCard — seller-facing UI to configure Twilio WhatsApp/SMS
 * pings that fire when a high-intent buyer hits checkout on one of
 * their SKUs.
 *
 * Sits at the top of /workspace/intent-radar so sellers arm this the
 * first time they land on the buyer prediction engine.
 */
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bell, BellOff, Loader2, MessageCircle, Phone, Save, Send, Clock,
  TrendingUp, ShieldCheck, Zap, Unlock, KeyRound, Calendar, Eye, Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface AlertConfig {
  enabled: boolean;
  channel: "whatsapp" | "sms";
  phone: string;
  min_intent: number;
  cooldown_minutes: number;
  quiet_hours_start: number | null;
  quiet_hours_end: number | null;
  workspace_id: string | null;
  digest_enabled?: boolean;
  digest_hour_utc?: number;
  digest_timezone?: string;
  digest_min_intent?: number;
  last_digest_sent_at?: string | null;
  weekly_digest_enabled?: boolean;
  weekly_digest_dow?: number;
  last_weekly_digest_sent_at?: string | null;
}

interface DigestPreview {
  empty: boolean;
  text?: string;
  buyers_count?: number;
  products_count?: number;
  min_intent?: number;
  reason?: string;
  message?: string;
  generated_at?: string;
}

interface Counters {
  delivered_all_time: number;
  delivered_24h: number;
  delivered_7d: number;
  skipped_24h: number;
}

interface RecentAlert {
  buyer_user_id: string | null;
  product_id: string | null;
  product_title?: string | null;
  event_type: string;
  intent_score: number | null;
  channel: string;
  phone: string;
  body_preview: string;
  status: "delivered" | "failed" | "skipped";
  skip_reason?: string | null;
  twilio_error?: string | null;
  reply_status?: string | null;
  reply_unlocked_at?: string | null;
  created_at: string;
}

interface ReplyUnlock {
  buyer_user_id: string;
  product_id: string;
  from_number: string;
  body: string;
  credits_charged: number;
  fresh_unlock: boolean;
  created_at: string;
}

// --------------- Timezone helpers ---------------
const CURATED_TZS = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo",
  "Asia/Shanghai", "Asia/Hong_Kong", "Asia/Bangkok", "Asia/Jakarta",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Amsterdam",
  "Europe/Madrid", "Europe/Rome",
  "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Toronto", "America/Sao_Paulo",
  "America/Mexico_City",
  "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
  "Africa/Johannesburg", "Africa/Cairo", "Africa/Lagos",
  "UTC",
];

function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch { return "UTC"; }
}

function allTimezones(): string[] {
  try {
    const all = (Intl as any).supportedValuesOf?.("timeZone");
    if (Array.isArray(all) && all.length) return all;
  } catch { /* fall through */ }
  return CURATED_TZS;
}

/** Offset of `tz` at `date`, in minutes (positive = ahead of UTC). */
function tzOffsetMinutes(date: Date, tz: string): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(date);
    const filled: Record<string, string> = {};
    for (const p of parts) filled[p.type] = p.value;
    // Intl 24-hour clock returns "24" for midnight in some browsers.
    const hh = filled.hour === "24" ? "00" : filled.hour;
    const asUtc = Date.UTC(
      +filled.year, +filled.month - 1, +filled.day,
      +hh, +filled.minute, +filled.second
    );
    return (asUtc - date.getTime()) / 60000;
  } catch { return 0; }
}

/** Convert `localHour` in `fromTz` → the equivalent local hour in `toTz`.
 *  Rounds to the nearest integer hour and wraps into 0-23. */
function convertHour(localHour: number, fromTz: string, toTz: string): number {
  const now = new Date();
  const fromOff = tzOffsetMinutes(now, fromTz);
  const toOff = tzOffsetMinutes(now, toTz);
  const hourUtc = localHour - fromOff / 60;
  let h = hourUtc + toOff / 60;
  h = Math.round(h);
  h = ((h % 24) + 24) % 24;
  return h;
}

/** Pretty label for the digest hour in a given TZ, e.g. "14:00 · Asia/Kolkata". */
function fmtHourInTz(hour: number, tz: string): string {
  return `${String(hour).padStart(2, "0")}:00 · ${tz}`;
}

export const PushAlertsCard: React.FC = () => {
  const qc = useQueryClient();

  const cfg = useQuery<AlertConfig>({
    queryKey: ["push-alerts-config"],
    queryFn: async () => (await api.get("/push-alerts/config")).data,
  });
  const kpi = useQuery<Counters>({
    queryKey: ["push-alerts-counters"],
    queryFn: async () => (await api.get("/push-alerts/counters")).data,
    refetchInterval: 60_000,
  });
  const recent = useQuery<{ alerts: RecentAlert[] }>({
    queryKey: ["push-alerts-recent"],
    queryFn: async () => (await api.get("/push-alerts/recent?limit=10")).data,
    refetchInterval: 60_000,
  });
  const unlocks = useQuery<{ unlocks: ReplyUnlock[] }>({
    queryKey: ["push-alerts-reply-unlocks"],
    queryFn: async () => (await api.get("/push-alerts/reply-unlocks?limit=5")).data,
    refetchInterval: 60_000,
  });

  const [draft, setDraft] = useState<Partial<AlertConfig>>({});
  const [testPhone, setTestPhone] = useState("");

  useEffect(() => {
    if (cfg.data) {
      setDraft({});
      setTestPhone(cfg.data.phone || "");
    }
  }, [cfg.data]);

  function valueOf<K extends keyof AlertConfig>(key: K): AlertConfig[K] {
    if (Object.prototype.hasOwnProperty.call(draft, key)) return (draft as any)[key];
    return (cfg.data as any)?.[key];
  }
  function update<K extends keyof AlertConfig>(key: K, value: AlertConfig[K]) {
    setDraft(d => ({ ...d, [key]: value }));
  }

  const save = useMutation({
    mutationFn: async () => (await api.put("/push-alerts/config", draft)).data,
    onSuccess: (data) => {
      qc.setQueryData(["push-alerts-config"], data);
      setDraft({});
      toast.success("Push alert settings saved.");
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const test = useMutation({
    mutationFn: async () =>
      (await api.post("/push-alerts/test", {
        channel: valueOf("channel"),
        phone: testPhone || valueOf("phone"),
      })).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["push-alerts-recent"] });
      qc.invalidateQueries({ queryKey: ["push-alerts-counters"] });
      if (data?.ok) toast.success(`Test ${data.channel} sent to ${data.phone}`);
      else toast.error(data?.error || "Twilio returned failure — check config");
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Test failed"),
  });

  const [digestPreview, setDigestPreview] = useState<DigestPreview | null>(null);
  const previewDigest = useMutation({
    mutationFn: async () => (await api.get("/push-alerts/digest/preview")).data,
    onSuccess: (data: DigestPreview) => setDigestPreview(data),
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Preview failed"),
  });
  const sendDigest = useMutation({
    mutationFn: async () => (await api.post("/push-alerts/digest/send-now")).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["push-alerts-recent"] });
      qc.invalidateQueries({ queryKey: ["push-alerts-config"] });
      if (data?.ok) toast.success(`Digest sent · ${data.buyers_count} buyers · ${data.products_count} products`);
      else toast.error(data?.message || data?.error || "Digest send failed");
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Digest send failed"),
  });

  const [weeklyPreview, setWeeklyPreview] = useState<DigestPreview | null>(null);
  const previewWeekly = useMutation({
    mutationFn: async () => (await api.get("/push-alerts/digest/weekly/preview")).data,
    onSuccess: (data: DigestPreview) => setWeeklyPreview(data),
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Weekly preview failed"),
  });
  const sendWeekly = useMutation({
    mutationFn: async () => (await api.post("/push-alerts/digest/weekly/send-now")).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["push-alerts-recent"] });
      qc.invalidateQueries({ queryKey: ["push-alerts-config"] });
      if (data?.ok) toast.success(`Weekly rollup sent · ${data.buyers_count} buyers · ${data.products_count} products`);
      else toast.error(data?.message || data?.error || "Weekly send failed");
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Weekly send failed"),
  });

  if (cfg.isLoading) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 mb-4" data-testid="push-alerts-card">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  const enabled = !!valueOf("enabled");
  const dirty = Object.keys(draft).length > 0;

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden mb-4" data-testid="push-alerts-card">
      {/* Header strip */}
      <div className="px-5 py-4 border-b border-neutral-100 bg-gradient-to-r from-orange-50 to-fuchsia-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl grid place-items-center text-white shadow-sm ${
            enabled ? "bg-gradient-to-br from-emerald-500 to-teal-600 animate-pulse" : "bg-neutral-300"
          }`}>
            {enabled ? <Zap className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-neutral-500">
              Push-to-Seller Alerts
            </div>
            <div className="text-sm text-neutral-700">
              Twilio {valueOf("channel") === "whatsapp" ? "WhatsApp" : "SMS"} ping when a buyer with intent ≥{" "}
              <span className="font-semibold">{valueOf("min_intent") ?? 65}</span> hits checkout on your SKU.{" "}
              <span className="inline-flex items-center gap-1 text-fuchsia-700 font-semibold">
                <KeyRound className="h-3 w-3" />
                Reply <code className="font-mono bg-fuchsia-50 border border-fuchsia-200 rounded px-1">u</code> to any alert to auto-unlock the buyer's contact.
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">
            <TrendingUp className="h-3 w-3" />
            {kpi.data?.delivered_24h ?? 0} today · {kpi.data?.delivered_7d ?? 0} / 7d
          </div>
          <button
            data-testid="push-alerts-enabled-toggle"
            onClick={() => update("enabled", !enabled)}
            aria-pressed={enabled}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              enabled
                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                : "bg-neutral-200 text-neutral-700 hover:bg-neutral-300"
            }`}
          >
            {enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
            {enabled ? "Alerts ON" : "Alerts OFF"}
          </button>
        </div>
      </div>

      {/* Config body */}
      <div className="p-5 grid md:grid-cols-2 gap-5">
        {/* Left: channel + phone */}
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 font-bold">
              Delivery channel
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="push-alerts-channel-whatsapp"
                onClick={() => update("channel", "whatsapp")}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  valueOf("channel") === "whatsapp"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                    : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
                }`}
              >
                <MessageCircle className="h-3.5 w-3.5 inline-block mr-1" />
                WhatsApp
              </button>
              <button
                data-testid="push-alerts-channel-sms"
                onClick={() => update("channel", "sms")}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  valueOf("channel") === "sms"
                    ? "bg-indigo-50 text-indigo-700 border-indigo-300"
                    : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
                }`}
              >
                <Phone className="h-3.5 w-3.5 inline-block mr-1" />
                SMS
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 font-bold">
              Your alert number (E.164, with +)
            </div>
            <input
              data-testid="push-alerts-phone"
              value={valueOf("phone") || ""}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+919876543210"
              className="w-full px-3 py-2 rounded-lg text-sm border border-neutral-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/15 outline-none font-mono"
            />
            <div className="text-[10px] text-neutral-400 mt-1">
              Sandbox WhatsApp requires you to opt-in first by sending{" "}
              <code className="font-mono">join &lt;code&gt;</code> to Twilio's sandbox number.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="push-alerts-test-phone"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="Test to this number"
              className="flex-1 px-3 py-2 rounded-lg text-sm border border-neutral-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/15 outline-none font-mono"
            />
            <Button
              data-testid="push-alerts-test-btn"
              size="sm"
              variant="outline"
              onClick={() => test.mutate()}
              disabled={test.isPending || !testPhone.trim()}
            >
              {test.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
              Send test
            </Button>
          </div>
        </div>

        {/* Right: threshold + cooldown */}
        <div className="space-y-3">
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                Intent threshold
              </div>
              <div className="text-xs font-mono text-neutral-800">
                {valueOf("min_intent") ?? 65}/100
              </div>
            </div>
            <input
              type="range"
              min={40}
              max={95}
              step={1}
              value={valueOf("min_intent") ?? 65}
              onChange={(e) => update("min_intent", parseInt(e.target.value, 10))}
              data-testid="push-alerts-threshold"
              className="w-full accent-fuchsia-600"
            />
            <div className="text-[10px] text-neutral-400 mt-0.5">
              Only buyers scoring at least this intent trigger a ping. Lower = more
              alerts, higher = only the hottest.
            </div>
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                <Clock className="h-3 w-3 inline-block mr-1" />
                Cooldown per buyer
              </div>
              <div className="text-xs font-mono text-neutral-800">
                {valueOf("cooldown_minutes") ?? 30} min
              </div>
            </div>
            <input
              type="range"
              min={5}
              max={240}
              step={5}
              value={valueOf("cooldown_minutes") ?? 30}
              onChange={(e) => update("cooldown_minutes", parseInt(e.target.value, 10))}
              data-testid="push-alerts-cooldown"
              className="w-full accent-fuchsia-600"
            />
            <div className="text-[10px] text-neutral-400 mt-0.5">
              We won't ping you again about the same buyer within this window.
            </div>
          </div>
          <Button
            data-testid="push-alerts-save-btn"
            size="sm"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="w-full bg-neutral-900 text-white hover:bg-black"
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save settings{dirty ? " *" : ""}
          </Button>
        </div>
      </div>

      {/* Daily digest sub-panel */}
      <div
        data-testid="push-alerts-digest-panel"
        className="px-5 py-4 border-t border-neutral-100 bg-gradient-to-r from-indigo-50/50 to-fuchsia-50/50"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-lg grid place-items-center text-white ${
              valueOf("digest_enabled") ? "bg-gradient-to-br from-indigo-500 to-fuchsia-600" : "bg-neutral-300"
            }`}>
              <Calendar className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-neutral-500">
                Daily digest · near-hot buyers rollup
              </div>
              <div className="text-xs text-neutral-600 max-w-xl">
                One evening WhatsApp/SMS summarising every buyer who came close to checkout
                today. Perfect for skimming without opening the app.
                {valueOf("last_digest_sent_at") && (
                  <span className="ml-1 text-emerald-700 font-semibold">
                    · last sent {new Date(valueOf("last_digest_sent_at")!).toLocaleString([],
                      { dateStyle: "medium", timeStyle: "short" } as any)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            data-testid="push-alerts-digest-toggle"
            onClick={() => update("digest_enabled", !valueOf("digest_enabled"))}
            aria-pressed={!!valueOf("digest_enabled")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              valueOf("digest_enabled")
                ? "bg-indigo-500 text-white hover:bg-indigo-600"
                : "bg-neutral-200 text-neutral-700 hover:bg-neutral-300"
            }`}
          >
            <Calendar className="h-3.5 w-3.5" />
            {valueOf("digest_enabled") ? "Digest ON" : "Digest OFF"}
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                Send time
              </div>
              <div className="text-xs font-mono text-neutral-800">
                {fmtHourInTz(valueOf("digest_hour_utc") ?? 14, valueOf("digest_timezone") || "UTC")}
                <span className="ml-1 text-neutral-400">
                  · {String(convertHour(valueOf("digest_hour_utc") ?? 14, valueOf("digest_timezone") || "UTC", "Asia/Kolkata")).padStart(2, "0")}:00 IST
                </span>
              </div>
            </div>
            <input
              type="range"
              min={0}
              max={23}
              step={1}
              value={valueOf("digest_hour_utc") ?? 14}
              onChange={(e) => update("digest_hour_utc", parseInt(e.target.value, 10))}
              data-testid="push-alerts-digest-hour"
              className="w-full accent-indigo-600"
            />
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1 flex items-center gap-1">
                <Globe className="h-3 w-3" /> Your timezone
              </div>
              <input
                data-testid="push-alerts-digest-timezone"
                list="digest-tz-list"
                value={valueOf("digest_timezone") || ""}
                placeholder={browserTz()}
                onChange={(e) => {
                  const newTz = e.target.value.trim();
                  const oldTz = valueOf("digest_timezone") || "UTC";
                  const curHour = valueOf("digest_hour_utc") ?? 14;
                  // Auto-convert: preserve effective UTC firing time by
                  // shifting the local hour into the new TZ.
                  if (newTz && newTz !== oldTz) {
                    try {
                      // Validate by asking Intl to format in the target zone.
                      new Intl.DateTimeFormat("en-US", { timeZone: newTz }).format(new Date());
                      const newHour = convertHour(curHour, oldTz, newTz);
                      setDraft(d => ({ ...d, digest_timezone: newTz, digest_hour_utc: newHour }));
                      return;
                    } catch {
                      // Not a valid IANA zone yet (user still typing) — just store the string.
                    }
                  }
                  update("digest_timezone", newTz);
                }}
                className="w-full px-2 py-1.5 rounded-lg text-xs border border-neutral-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15 outline-none font-mono"
              />
              <datalist id="digest-tz-list">
                {allTimezones().map((z) => (
                  <option key={z} value={z} />
                ))}
              </datalist>
              <button
                type="button"
                data-testid="push-alerts-digest-tz-detect"
                onClick={() => {
                  const detected = browserTz();
                  const oldTz = valueOf("digest_timezone") || "UTC";
                  const curHour = valueOf("digest_hour_utc") ?? 14;
                  if (detected && detected !== oldTz) {
                    const newHour = convertHour(curHour, oldTz, detected);
                    setDraft(d => ({ ...d, digest_timezone: detected, digest_hour_utc: newHour }));
                  } else {
                    update("digest_timezone", detected);
                  }
                }}
                className="mt-1 text-[10px] text-indigo-600 hover:text-indigo-800 underline"
              >
                Use my browser timezone ({browserTz()})
              </button>
            </div>
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                Include buyers with intent ≥
              </div>
              <div className="text-xs font-mono text-neutral-800">
                {valueOf("digest_min_intent") ?? 40}/100
              </div>
            </div>
            <input
              type="range"
              min={0}
              max={95}
              step={5}
              value={valueOf("digest_min_intent") ?? 40}
              onChange={(e) => update("digest_min_intent", parseInt(e.target.value, 10))}
              data-testid="push-alerts-digest-min-intent"
              className="w-full accent-indigo-600"
            />
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button
            data-testid="push-alerts-digest-preview-btn"
            variant="outline"
            size="sm"
            onClick={() => previewDigest.mutate()}
            disabled={previewDigest.isPending}
          >
            {previewDigest.isPending
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <Eye className="h-3.5 w-3.5 mr-1" />}
            Preview digest
          </Button>
          <Button
            data-testid="push-alerts-digest-send-btn"
            size="sm"
            onClick={() => sendDigest.mutate()}
            disabled={sendDigest.isPending || !valueOf("phone")}
            className="bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {sendDigest.isPending
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <Send className="h-3.5 w-3.5 mr-1" />}
            Send digest now
          </Button>
          {digestPreview && (
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
              {digestPreview.empty
                ? "preview: empty"
                : `preview: ${digestPreview.buyers_count} buyers · ${digestPreview.products_count} products`}
            </span>
          )}
        </div>

        {digestPreview && !digestPreview.empty && digestPreview.text && (
          <pre
            data-testid="push-alerts-digest-preview-text"
            className="mt-3 rounded-lg bg-white border border-neutral-200 p-3 text-[11px] leading-snug text-neutral-700 whitespace-pre-wrap font-mono max-h-56 overflow-auto"
          >
            {digestPreview.text}
          </pre>
        )}
        {digestPreview && digestPreview.empty && (
          <div
            data-testid="push-alerts-digest-preview-empty"
            className="mt-3 rounded-lg bg-white border border-dashed border-neutral-300 p-3 text-[11px] text-neutral-500"
          >
            {digestPreview.message}
          </div>
        )}
      </div>

      {/* Weekly rollup sub-panel */}
      <div
        data-testid="push-alerts-weekly-panel"
        className="px-5 py-4 border-t border-neutral-100 bg-gradient-to-r from-violet-50/60 to-fuchsia-50/40"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-lg grid place-items-center text-white ${
              valueOf("weekly_digest_enabled") ? "bg-gradient-to-br from-violet-500 to-fuchsia-600" : "bg-neutral-300"
            }`}>
              <Calendar className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-neutral-500">
                Weekly rollup · 7-day summary
              </div>
              <div className="text-xs text-neutral-600 max-w-xl">
                Lower-frequency drumbeat — one WhatsApp/SMS every week with your 7-day
                near-hot buyer summary. Perfect for founders who prefer a Monday-morning briefing over a daily ping.
                {valueOf("last_weekly_digest_sent_at") && (
                  <span className="ml-1 text-emerald-700 font-semibold">
                    · last sent {new Date(valueOf("last_weekly_digest_sent_at")!).toLocaleString([],
                      { dateStyle: "medium", timeStyle: "short" } as any)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            data-testid="push-alerts-weekly-toggle"
            onClick={() => update("weekly_digest_enabled", !valueOf("weekly_digest_enabled"))}
            aria-pressed={!!valueOf("weekly_digest_enabled")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              valueOf("weekly_digest_enabled")
                ? "bg-violet-500 text-white hover:bg-violet-600"
                : "bg-neutral-200 text-neutral-700 hover:bg-neutral-300"
            }`}
          >
            <Calendar className="h-3.5 w-3.5" />
            {valueOf("weekly_digest_enabled") ? "Weekly ON" : "Weekly OFF"}
          </button>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 font-bold">
            Send on
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="push-alerts-weekly-dow-group">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((name, dow) => {
              const active = (valueOf("weekly_digest_dow") ?? 0) === dow;
              return (
                <button
                  key={dow}
                  data-testid={`push-alerts-weekly-dow-${dow}`}
                  onClick={() => update("weekly_digest_dow", dow as any)}
                  aria-pressed={active}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                    active
                      ? "bg-violet-500 text-white border-violet-500"
                      : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
          <div className="text-[10px] text-neutral-400 mt-1">
            Fires at the same hour as the daily digest ({String(valueOf("digest_hour_utc") ?? 14).padStart(2, "0")}:00 in {valueOf("digest_timezone") || "UTC"}).
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button
            data-testid="push-alerts-weekly-preview-btn"
            variant="outline"
            size="sm"
            onClick={() => previewWeekly.mutate()}
            disabled={previewWeekly.isPending}
          >
            {previewWeekly.isPending
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <Eye className="h-3.5 w-3.5 mr-1" />}
            Preview weekly
          </Button>
          <Button
            data-testid="push-alerts-weekly-send-btn"
            size="sm"
            onClick={() => sendWeekly.mutate()}
            disabled={sendWeekly.isPending || !valueOf("phone")}
            className="bg-violet-600 text-white hover:bg-violet-700"
          >
            {sendWeekly.isPending
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <Send className="h-3.5 w-3.5 mr-1" />}
            Send weekly now
          </Button>
          {weeklyPreview && (
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
              {weeklyPreview.empty
                ? "preview: empty"
                : `preview: ${weeklyPreview.buyers_count} buyers · ${weeklyPreview.products_count} products · 7d`}
            </span>
          )}
        </div>

        {weeklyPreview && !weeklyPreview.empty && weeklyPreview.text && (
          <pre
            data-testid="push-alerts-weekly-preview-text"
            className="mt-3 rounded-lg bg-white border border-neutral-200 p-3 text-[11px] leading-snug text-neutral-700 whitespace-pre-wrap font-mono max-h-56 overflow-auto"
          >
            {weeklyPreview.text}
          </pre>
        )}
        {weeklyPreview && weeklyPreview.empty && (
          <div
            data-testid="push-alerts-weekly-preview-empty"
            className="mt-3 rounded-lg bg-white border border-dashed border-neutral-300 p-3 text-[11px] text-neutral-500"
          >
            {weeklyPreview.message}
          </div>
        )}
      </div>

      {/* Recent alerts */}
      {(recent.data?.alerts?.length ?? 0) > 0 && (
        <div className="px-5 py-4 border-t border-neutral-100 bg-neutral-50" data-testid="push-alerts-recent">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
              Recent alert activity
            </div>
            {(kpi.data?.skipped_24h ?? 0) > 0 && (
              <div className="text-[10px] text-neutral-400">
                {kpi.data?.skipped_24h} skipped in last 24h (cooldown / threshold)
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            {recent.data!.alerts.slice(0, 5).map((a, i) => (
              <div
                key={i}
                data-testid={`push-alerts-row-${i}`}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-xs bg-white border ${
                  a.reply_status === "unlocked" ? "border-fuchsia-300 ring-1 ring-fuchsia-200"
                    : a.status === "delivered" ? "border-emerald-200"
                    : a.status === "failed" ? "border-rose-200"
                    : "border-slate-200"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${
                  a.status === "delivered" ? "bg-emerald-500 animate-pulse"
                    : a.status === "failed" ? "bg-rose-500"
                    : "bg-slate-400"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-neutral-800 truncate flex items-center gap-1.5">
                    {a.event_type.replace(/_/g, " ")}
                    {a.product_title && ` · ${a.product_title}`}
                    {a.reply_status === "unlocked" && (
                      <span
                        data-testid={`push-alerts-row-${i}-unlocked`}
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-1.5 shrink-0"
                      >
                        <Unlock className="h-2.5 w-2.5" /> unlocked via reply
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 truncate">
                    {a.intent_score != null ? `intent ${a.intent_score}/100 · ` : ""}
                    {a.channel} → {a.phone}
                    {a.skip_reason ? ` · skipped: ${a.skip_reason}` : ""}
                    {a.twilio_error ? ` · ${a.twilio_error}` : ""}
                  </div>
                </div>
                <span className="text-[10px] text-neutral-400 font-mono shrink-0">
                  {new Date(a.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
          {(unlocks.data?.unlocks?.length ?? 0) > 0 && (
            <div className="mt-3 pt-3 border-t border-neutral-200" data-testid="push-alerts-reply-unlocks">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold mb-1.5 flex items-center gap-1">
                <Unlock className="h-3 w-3" /> Reply unlocks · buyers you revealed by texting back
              </div>
              <div className="flex flex-wrap gap-1.5">
                {unlocks.data!.unlocks.slice(0, 5).map((u, i) => (
                  <span
                    key={i}
                    data-testid={`push-alerts-reply-unlock-${i}`}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200"
                    title={`Reply "${u.body}" → ${u.credits_charged} credits charged${u.fresh_unlock ? "" : " (already unlocked, no charge)"}`}
                  >
                    <KeyRound className="h-2.5 w-2.5" />
                    {new Date(u.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    · {u.credits_charged}c
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!enabled && (
        <div className="px-5 py-3 border-t border-neutral-100 bg-amber-50/60 text-[11px] text-amber-800 flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" />
          Alerts are OFF. Flip the toggle above to arm real-time seller pings.
        </div>
      )}
    </div>
  );
};

export default PushAlertsCard;
